import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  Building2,
  Package,
  Boxes,
  Truck,
  HardHat,
  Wallet,
  ArrowRightLeft,
  Coins,
  IndianRupee,
  FileText,
  TrendingUp,
  ArrowRight,
  Calendar,
} from 'lucide-react';
import { type LucideIcon } from 'lucide-react';
import { REPORT_CARDS } from '../lib/reports';
import Shell from '../components/Shell';

const ICON_MAP: Record<string, LucideIcon> = {
  client: Users,
  project: Building2,
  daily: Calendar,
  material: Package,
  stock: Boxes,
  supplier: Truck,
  subcontractor: HardHat,
  payment: Wallet,
  transfer: ArrowRightLeft,
  finance: IndianRupee,
};

export default function Reports() {
  const navigate = useNavigate();

  return (
    <Shell title="Reports & Financial Insights" crumb="Insights">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <p style={{ marginTop: 0, marginBottom: 22, color: 'var(--text-soft)', fontSize: 13.5 }}>
          Comprehensive financial audits, site consumption ledgers, and operational reports.
          Every report includes approved transactions only and can be filtered by date range and exported to Excel or PDF.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
          }}
        >
          {REPORT_CARDS.map((r, i) => {
            const IconComp = ICON_MAP[r.key] || FileText;

            return (
              <motion.button
                key={r.key}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: i * 0.03 }}
                whileHover={{ y: -2 }}
                onClick={() => navigate(`/reports/${r.key}`)}
                style={{
                  textAlign: 'left',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  padding: '20px 22px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  fontFamily: 'inherit',
                  color: 'var(--text)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                }}
              >
                {/* Top Icon */}
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 'var(--radius-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(37, 99, 235, 0.08)',
                    color: 'var(--brand)',
                    border: '1px solid rgba(37, 99, 235, 0.15)',
                  }}
                >
                  <IconComp size={20} />
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontFamily: 'var(--font-ui)',
                      fontWeight: 700,
                      fontSize: 15,
                      color: 'var(--text)',
                    }}
                  >
                    {r.title}
                  </div>
                  <div
                    style={{
                      fontSize: 12.5,
                      color: 'var(--text-soft)',
                      lineHeight: 1.5,
                      marginTop: 4,
                    }}
                  >
                    {r.description}
                  </div>
                </div>

                {/* Bottom Action Footer */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 6,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: 'var(--accent)',
                    paddingTop: 10,
                    borderTop: '1px solid var(--border-subtle)',
                    marginTop: 4,
                  }}
                >
                  <span>Open Report</span>
                  <ArrowRight size={14} />
                </div>
              </motion.button>
            );
          })}
        </div>
      </motion.div>
    </Shell>
  );
}
